/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/Base.type';
import { page as SamplePage } from '../../help/sample_pages/CardPage';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Card';

const CardManager: ComponentManagerFn<'Card'> = (
  platform: Platform,
): ComponentManager<'Card'> => {
  return {
    ...baseComponentManager<'Card'>(platform, 'Card'),
    defaultValue,
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'CardHeader',
            'CardContent',
            'CardActions',
            'TriggerOnClick',
            'PulseTip',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    props: {
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: false,
      },
      raised: { label: 'Raised', type: 'boolean', category: 'Look & Feel' },
      border: { label: 'Border', type: 'boolean', category: 'Look & Feel' },
      collapsible: {
        label: 'Collapsible',
        type: 'boolean',
        category: 'Look & Feel',
        helpText:
          'Select this property if collapsible is needed for card content to toggle.',
      },
      collapsed: {
        label: 'Collapsed',
        type: 'boolean',
        category: 'Look & Feel',
        helpText: 'Select this property to collapse card content',
      },
      cardHeight: {
        label: 'Height',
        type: 'string',
      },
      showOnlyOnHoverItemIds: {
        label: 'Show Only On Hover',
        type: 'MultiItemID',
        validateItemId: (value, { platform: p, pageId, appUid, itemId }) => {
          const found = p.findParent(
            appUid,
            pageId,
            value,
            (c) => c.itemId === itemId,
          );
          if (!found) {
            return `Unable to find a child component with this item id '${value}' inside this Card!`;
          }
          return '';
        },
        helpText:
          'Choose one or more Item IDs of the child components to be only visible on the mouse hover of this Card component.',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'border':
          return !itemData.raised;
        default:
          return true;
      }
    },
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    noOverlay: true,
    defaultChildren: ['CardHeader', 'CardContent', 'CardActions'],
    onChildMoveIn: ({ page, value }) => {
      // CardHeader must be the first child
      // CardActions must be the last child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (aItem.type === 'CardHeader' || bItem.type === 'CardActions') {
            return -1;
          }
          if (bItem.type === 'CardHeader' || aItem.type === 'CardActions') {
            return 1;
          }
        }
        return 0;
      });
    },
    icon: { iconName: 'io-card', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Card component is a highly structured container used to group related information into a single, cohesive unit. Unlike generic wrappers, the Card enforces a standard layout composition—typically consisting of a Header, Content Body, and Action Footer. This structure makes it ideal for building dashboard widgets, user profile summaries, or data-rich tiles that require a consistent hierarchy across your application.

**1. Structured Content Composition**<br/>
The Card is designed to maintain a strict visual order, ensuring your UI remains consistent regardless of the content inside. When you add children to a Card, they serve specific roles:
* **Card Header:** Always anchored to the top. Use this for titles, avatars, or primary context.
* **Card Content:** Occupies the central area. This is the flexible container for your main data, charts, or text.
* **Card Actions:** Always anchored to the bottom. Use this for action buttons (like "View Details" or "Submit") to provide a clear next step for the user.
* **Note:** The Card allows only one instance of each child type to prevent layout conflicts.

**2. Visual Style & Elevation**<br/>
These settings control the aesthetic "weight" of the card on the page, helping you distinguish between flat content and interactive elements.
* **Raised:** Adds a drop shadow (elevation) to the card, making it appear to float above the background. Use this to draw attention to important items.
* **Border:** Adds a solid outline around the card. This is useful for separating content in "flat" designs where shadows are not used. (Note: Typically disabled if 'Raised' is enabled).
* **BG Color:** Applies a specific background color to the card from the predefined palette.
* **Height:** Sets a fixed vertical size for the card. If left undefined, the card will grow naturally to fit its content.

**3. Interactivity & Collapsibility**<br/>
Cards can be configured to behave dynamically, allowing users to focus on high-level summaries while keeping details accessible on demand.
* **Collapsible:** Enables a toggle mechanism (usually within the header) that allows the user to expand or hide the Card Content section. This is essential for decluttering complex dashboards.
* **Collapsed:** Determines the initial state of the card when the page loads. Enable this to start with the content hidden.
* **Show Only On Hover:** A powerful visibility control that lets you select specific child components to remain hidden until the user interacts with the card. This is perfect for revealing "Edit" or "Delete" buttons only when the user's mouse is over the card, keeping the UI clean.`,
    helpRenders: [
      {
        title: 'Basic Card',
        description:
          'A simple card with a header (with Avatar & Icon Button), actions (footer) and empty body.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 400,
        visibleProps: ['border', 'raised'],
        item: {
          children: ['cardHeader', 'cardContent', 'cardActions'],
          itemId: 'card',
          parent: 'root',
          props: {},
          type: 'Card',
        },
      },
    ],
  };
};

export default CardManager;
